/**
 * What a workflow thread is paused on, whose turn raised it, and how a card
 * says so. A step's own question or approval makes it Needs You; one a
 * background notice or a teammate's message raised only holds the steps
 * behind it, and their cards say what they wait on and how to unblock it.
 * Pure so the wording is testable.
 */
import type { UIMessage } from "ai";

import type { AgentThread } from "@/features/agents/api/agentApi";
import type { AgentMessageMetadata } from "@/features/agents/useAgentChat";

import { WORKFLOWS } from "./catalog";

export type WorkflowHold = {
  kind: "question" | "approval" | "turn";
  /** The question's title or first prompt, or the change asked for. */
  summary: string | null;
  /** Where the paused turn came from: "Organize Into Folders' background sort". */
  source: string | null;
  threadId: string;
  /** The step's own turn raised it. */
  own: boolean;
};

/** The question on one line, whole: the card wraps it. */
export const shortSummary = (text: string): string =>
  text
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[.?!:]+$/, "");

/** Background runs whose notice wakes Casey on the thread that started them. */
const NOTICE_SOURCES: Record<string, string> = {
  autocategorize: "Organize Into Folders' background sort",
  titling: "Retitle Exhibits' background titling",
  pdf_draft: "a letter being drafted",
  form_fill: "a form being filled",
  packet: "the packet build",
  export: "an export"
};

type Meta = AgentMessageMetadata & {
  author?: { kind?: string; name?: string };
};

/** Where the turn that `messageId` belongs to came from: its prompt's sender. */
const sourceOf = (
  messages: UIMessage[],
  messageId: string | null | undefined
): { triggerId: string | null; source: string | null } => {
  const at = messageId ? messages.findIndex((m) => m.id === messageId) : -1;
  const end = at >= 0 ? at : messages.length;
  for (let i = end - 1; i >= 0; i -= 1) {
    const m = messages[i];
    // eslint-disable-next-line no-continue
    if (m.role === "assistant") continue;
    const meta = (m.metadata ?? {}) as Meta;
    const step = meta.workflow
      ? WORKFLOWS.find((w) => w.id === meta.workflow?.id)
      : undefined;
    if (step) return { triggerId: m.id, source: step.title };
    if (meta.event?.kind || meta.author?.kind === "system")
      return {
        triggerId: m.id,
        source: NOTICE_SOURCES[meta.event?.kind ?? ""] ?? "a background update"
      };
    return {
      triggerId: m.id,
      source: meta.author?.name ? `${meta.author.name}'s message` : null
    };
  }
  return { triggerId: null, source: null };
};

const toolPhrase = (name: unknown) =>
  typeof name === "string" && name ? name.replace(/_/g, " ") : "make a change";

/**
 * What the thread waits on, if anything. `sentId` is the step's prompt: the
 * hold is the step's own when its turn raised it. With no prompt sent yet,
 * nothing on the thread is the step's.
 */
export const holdOf = (
  thread: Pick<AgentThread, "id" | "pending_question" | "pending_approval">,
  messages: UIMessage[],
  sentId: string | null = null
): WorkflowHold | null => {
  const q = thread.pending_question;
  const approval = thread.pending_approval as {
    calls?: { tool_name?: string }[];
    message_id?: string | null;
  } | null;
  if (!q && !approval?.calls?.length) return null;
  const { triggerId, source } = sourceOf(
    messages,
    q ? q.message_id : approval?.message_id
  );
  // A pause whose turn can't be placed counts as the step's own once it sent.
  const own = sentId !== null && (triggerId === null || triggerId === sentId);
  if (q) {
    const first = q.title?.trim() || q.questions[0]?.prompt?.trim() || "";
    return {
      kind: "question",
      summary: first ? shortSummary(first) : null,
      source,
      threadId: thread.id,
      own
    };
  }
  const calls = approval?.calls ?? [];
  return {
    kind: "approval",
    summary: `${toolPhrase(calls[0]?.tool_name)}${
      calls.length > 1 ? ` and ${calls.length - 1} more` : ""
    }`,
    source,
    threadId: thread.id,
    own
  };
};

/**
 * The transcript ends on a question the thread's record doesn't carry yet:
 * a hold on the last reply, the step's own when that reply answers `sentId`.
 */
export const transcriptHold = (
  threadId: string,
  messages: UIMessage[],
  sentId: string | null = null
): WorkflowHold => {
  const reply = [...messages].reverse().find((m) => m.role === "assistant");
  const { triggerId, source } = sourceOf(messages, reply?.id);
  return {
    kind: "question",
    summary: null,
    source,
    threadId,
    own: sentId !== null && (triggerId === null || triggerId === sentId)
  };
};

/** Another turn is running on the thread: what it's answering. */
export const busyHold = (
  threadId: string,
  messages: UIMessage[]
): WorkflowHold => ({
  kind: "turn",
  summary: null,
  source: sourceOf(messages, null).source,
  threadId,
  own: false
});

/** The card's text for a hold: a short detail, the reason and the way out. */
export type HoldText = {
  detail: string;
  reasons: string[];
  openLabel: string;
};

const from = (source: string | null) => (source ? ` (from ${source})` : "");
const quoted = (summary: string | null) => (summary ? ` "${summary}"` : "");

export const holdText = (hold: WorkflowHold): HoldText => {
  if (hold.kind === "approval")
    return hold.own
      ? {
          detail: "Approve Casey's change",
          reasons: [
            `Casey wants to ${hold.summary ?? "make a change"}.`,
            "Approve or decline it in chat to continue."
          ],
          openLabel: "Open Approval"
        }
      : {
          detail: "Waiting on a change to approve",
          reasons: [
            `Casey wants to ${hold.summary ?? "make a change"}${from(
              hold.source
            )}.`,
            "Approve or decline it in chat to continue."
          ],
          openLabel: "Open Approval"
        };
  if (hold.kind === "question")
    return hold.own
      ? {
          detail: `Casey asked ${
            hold.summary ? `"${hold.summary}"` : "a question"
          }`,
          reasons: [
            `Casey asked${quoted(hold.summary)}.`,
            "Answer it in chat to continue."
          ],
          openLabel: "Open Question"
        }
      : {
          detail: hold.summary
            ? `Waiting on "${hold.summary}"`
            : "Waiting on Casey's question",
          reasons: [
            `Casey asked${quoted(hold.summary)}${from(hold.source)}.`,
            "Answer it in chat to continue."
          ],
          openLabel: "Open Question"
        };
  return {
    detail: "Waiting for Casey to finish",
    reasons: [
      `Casey is replying${
        hold.source ? ` to ${hold.source}` : ""
      } on the workflow thread. This step starts when it's done.`
    ],
    openLabel: "Open Chat"
  };
};
